import * as React from 'react';
import { FC, useCallback, useEffect, useMemo } from 'react';

import { useRouter } from 'expo-router';

import { CardModal } from 'components/card-modal';
import { useRoute } from 'hooks/navigation/use-route';
import { useIsDesktopBreakpoint } from 'hooks/use-media-query';
import { CustomEventNames, logQuestEvent } from 'state/amplitude';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import noop from 'utils/noop';
import { routes } from 'utils/routing';

import { useCmsQuestDetailLazyQuery } from '../cms-hooks/use-cms-quest-detail';
import { QuestProvider } from '../loyalty-quest';
import { parseQuestActivationSource } from '../loyalty-quest/quest-activation-source';
import { useActivateQuest } from '../loyalty-quest/use-activate-quest';
import { canActivateQuest } from '../loyalty-quest/utils';
import { QuestCardSkeleton } from '../quest-card';
import { QuestDrawerContent } from '../quest-drawer-content';
import { QuestDetailError } from '../quest-drawer-content/quest-detail-error';
import { QuestDrawerSkeleton } from '../quest-drawer-content/quest-detail-skeleton';
import { useParseQuestCardData } from '../use-parse-quest-card-data';
import { useUserQuest } from '../use-user-quest';
import { getProductRequirementCmsIds, isPurchaseQuest } from '../utils';

export const QuestDetailsPage: FC<React.PropsWithChildren<{}>> = () => {
  const { isLoading, userQuest, cmsQuestCardUI } = useUserQuest();
  const { params } = useRoute<{ previousPath?: string; activationSource?: string }>();
  const { replace } = useRouter();
  const { loyaltyUser } = useLoyaltyUser();
  const activationSource = parseQuestActivationSource(params.activationSource);

  const isDesktop = useIsDesktopBreakpoint();

  const [
    activateQuest,
    { loading: loadingActivateQuestMutation, error: activateQuestError },
  ] = useActivateQuest({
    questId: userQuest?.loyaltyQuest.id,
  });
  const questRequirementCmsIds = useMemo(
    () => getProductRequirementCmsIds(userQuest?.loyaltyQuest),
    [userQuest?.loyaltyQuest]
  );
  const [
    cmsQuestDetailQuery,
    {
      loadingCmsQuestDetail,
      cmsQuestDetail,
      cmsQuestDetailUI,
      refetch: refetchCmsQuestDetail,
      error: cmsQuestDetailError,
    },
  ] = useCmsQuestDetailLazyQuery({
    cmsId: userQuest?.loyaltyQuest.cmsId,
    questRequirementCmsIds,
  });
  const { title } = useParseQuestCardData(
    userQuest?.loyaltyQuest,
    userQuest?.cmsQuestCard,
    cmsQuestCardUI,
    cmsQuestDetail
  );
  const questDetailError = activateQuestError || cmsQuestDetailError;
  const loadingQuestDetail = loadingCmsQuestDetail || loadingActivateQuestMutation;

  const logQuestActivationRequested = useCallback(() => {
    if (!userQuest?.loyaltyQuest || !userQuest?.cmsQuestCard) {
      return;
    }

    logQuestEvent(
      {
        name: CustomEventNames.QUEST_ACTIVATION_REQUESTED,
        quest: userQuest.loyaltyQuest,
        cmsQuestCard: userQuest.cmsQuestCard,
        attributes: {
          activationSource,
          loyaltyId: loyaltyUser?.id,
        },
      },
      { skipLoggingToBraze: true }
    );
  }, [activationSource, loyaltyUser?.id, userQuest?.cmsQuestCard, userQuest?.loyaltyQuest]);

  const onRefetch = useCallback(() => {
    if (canActivateQuest(userQuest?.loyaltyQuest.status)) {
      logQuestActivationRequested();
      activateQuest();
    }

    refetchCmsQuestDetail();
  }, [
    activateQuest,
    logQuestActivationRequested,
    refetchCmsQuestDetail,
    userQuest?.loyaltyQuest.status,
  ]);

  useEffect(() => {
    if (!loadingQuestDetail && userQuest?.loyaltyQuest.id && !questDetailError) {
      // If quest exists open the drawer and activate challenge
      const run = async () => {
        logQuestEvent(
          {
            name: CustomEventNames.QUEST_DETAILS_VIEW,
            quest: userQuest?.loyaltyQuest,
            cmsQuestCard: userQuest?.cmsQuestCard,
          },
          { skipLoggingToBraze: true }
        );

        if (canActivateQuest(userQuest?.loyaltyQuest.status)) {
          try {
            logQuestActivationRequested();
            await activateQuest();
          } catch {
            return;
          }
        }

        const detail = await cmsQuestDetailQuery();
        if (detail && isPurchaseQuest(detail)) {
          replace(routes.menu);
        }
      };

      run();
    }
  }, [
    activateQuest,
    cmsQuestDetailQuery,
    loadingQuestDetail,
    logQuestActivationRequested,
    questDetailError,
    replace,
    userQuest?.cmsQuestCard,
    userQuest?.loyaltyQuest,
  ]);

  if (isLoading) {
    return <QuestCardSkeleton />;
  }

  if (!userQuest || !cmsQuestCardUI) {
    return null;
  }

  const dismissType = params.previousPath?.includes(routes.loyaltyActivateQuest) ? 'back' : 'close';

  return (
    <CardModal
      modalContainerStyle={{
        borderRadius: 0,
        // For Android
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
      }}
      headerText={title}
      dismissType={dismissType}
      wrappedChildrenStyle={isDesktop ? { width: '100%', maxWidth: '100%' } : {}}
    >
      {questDetailError ? (
        <QuestDetailError refetch={onRefetch} />
      ) : (
        <QuestProvider quest={userQuest?.loyaltyQuest}>
          {loadingQuestDetail && <QuestDrawerSkeleton />}
          {!loadingQuestDetail && cmsQuestDetail && cmsQuestDetailUI && (
            <QuestDrawerContent
              cmsQuestDetail={cmsQuestDetail}
              cmsQuestDetailUI={cmsQuestDetailUI}
              cmsQuestCard={userQuest?.cmsQuestCard}
              onCloseDrawer={noop}
            />
          )}
        </QuestProvider>
      )}
    </CardModal>
  );
};
